<template>
  <div
    :class="$attrs.class"
    :style="[rootStyle, $attrs.style as any]"
  >
    <StatisticSuperGroupSection
      v-for="(group, index) in data"
      :key="group.key ?? ('super-group-' + index)"
      :group="group"
      :layout="layout"
      :selection-props="selectionProps"
      :size="size"
      :border="border"
      :equal-height="equalHeight"
      :reverse-color="reverseColor"
      :number-scroll="numberScroll"
      :sub-indicators-layout="subIndicatorsLayout"
      :theme="theme"
      @change="$emit('change', $event)"
      @update:value="$emit('update:value', $event)"
    />
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck SFC 顶层复杂联合 prop(value: string|number|Array)在 dts 生成器的 SFC 运行时编译中会被降级为 `type:[null,Array]`,触发 TS2769;源码类型已由 typecheck:web 保证,沿用 dts 生成器内置的 @ts-nocheck 逃逸
import { computed } from 'vue'
import StatisticSuperGroupSection from '../supporting/statistic-super-group-section.vue'
import type { StatisticCardSize, StatisticCardSelectionValue, PaginationConfig, StatisticCardItemData, StatisticVNode } from '../types'

defineOptions({ name: 'JdStatisticCardSuperGroup', inheritAttrs: false })

interface SelectionProps {
  controlType: 'radio' | 'checkbox' | null
  value?: StatisticCardSelectionValue | StatisticCardSelectionValue[]
  maxLength?: number
  minLength?: number
}

interface SuperGroupItem {
  key?: string | number
  label?: StatisticVNode
  description?: StatisticVNode
  extra?: StatisticVNode
  collapsible?: boolean
  defaultCollapsed?: boolean
  theme?: string
  columns?: number
  pagination?: PaginationConfig
  data: StatisticCardItemData[]
}

const props = withDefaults(
  defineProps<{
    data: SuperGroupItem[]
    layout?: 'horizontal' | 'vertical'
    gap?: number
    size?: StatisticCardSize
    border?: boolean
    equalHeight?: boolean
    reverseColor?: boolean
    numberScroll?: boolean
    subIndicatorsLayout?: 'horizontal' | 'vertical'
    theme?: string
    controlType?: 'radio' | 'checkbox' | null
    value?: StatisticCardSelectionValue | StatisticCardSelectionValue[]
    maxLength?: number
    minLength?: number
  }>(),
  {
    layout: 'horizontal',
    gap: 12,
    border: false,
    equalHeight: undefined,
    reverseColor: undefined,
    numberScroll: undefined,
  },
)

defineEmits<{
  (e: 'change', value: StatisticCardSelectionValue | StatisticCardSelectionValue[]): void
  (e: 'update:value', value: StatisticCardSelectionValue | StatisticCardSelectionValue[]): void
}>()

const selectionProps = computed<SelectionProps>(() => {
  if (props.controlType === 'radio') {
    return { controlType: 'radio', value: props.value }
  }
  if (props.controlType === 'checkbox') {
    return {
      controlType: 'checkbox',
      value: props.value,
      maxLength: props.maxLength,
      minLength: props.minLength,
    }
  }
  return { controlType: null }
})

// duplicate id check
const duplicateIds = computed(() => {
  const seen = new Set<string | number>()
  const duplicates = new Set<string | number>()
  props.data.forEach(group => {
    group.data.forEach((item) => {
      if (seen.has(item.id)) {
        duplicates.add(item.id)
        return
      }
      seen.add(item.id)
    })
  })
  return Array.from(duplicates)
})

if (selectionProps.value.controlType && duplicateIds.value.length > 0) {
  console.error(
    `[StatisticCard.SuperGroup] Detected duplicate card ids when selection is enabled: ${duplicateIds.value.join(', ')}. Card ids must be unique within the entire SuperGroup.`,
  )
}

const rootStyle = computed(() => ({
  display: 'flex',
  flexDirection: props.layout === 'horizontal' ? 'row' as const : 'column' as const,
  gap: `${props.gap}px`,
  width: '100%',
  minWidth: 0,
  alignItems: props.layout === 'horizontal' ? 'flex-start' : 'stretch',
}))
</script>
